import {Card} from 'platform/components';
import {Align, Box, Heading, Hide, Text, VStack} from 'platform/foundation';
import {useFormatPercentage} from 'platform/locale';

import {Trans} from 'react-i18next';

import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Alert} from '../../../../components/Alert/Alert';
import {AvoidBreakInside} from '../../../../components/AvoidBreakInside/AvoidBreakInside';
import i18n from '../../../../i18n/i18n';
import {MarketType} from '../../../../types/MarketType';
import {PriceReportType} from '../../../../types/PriceReportType';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {YearChart} from './components/YearChart';

interface StatisticsCardAgeProps extends TestIdProps {
  market?: MarketType;
  priceReport: PriceReportType;
}

export function StatisticsCardAge(props: StatisticsCardAgeProps) {
  const {isPrint} = useIsPrintForced();
  const formatPercentage = useFormatPercentage();

  const soldVehiclesInYears =
    props.market?.registrationYearChart?.statisticsAggregated?.map(({statistics, levels}) => ({
      amount: statistics.find((statistic) => statistic.type === 'sold')?.amount ?? 0,
      year: props.market?.registrationYearChart?.translations?.[
        levels.find((level) => level.name === 'year')?.value ?? ''
      ],
    })) ?? [];
  const maxSoldVehiclesByYear = Math.max(...soldVehiclesInYears.map(({amount}) => amount));
  const maxSoldVehiclesYears = soldVehiclesInYears
    .filter(({amount}) => amount === maxSoldVehiclesByYear)
    .map(({year}) => year)
    .join(', ');

  const soldVehiclesInMyYear = soldVehiclesInYears.find(
    ({year}) => year === String(props.priceReport.vehicle.firstRegistrationOnYear)
  )?.amount;

  const isMyVehicleSuccesYear = soldVehiclesInMyYear === maxSoldVehiclesByYear;

  if (
    isNilOrEmpty(props.market?.registrationYearChart?.statisticsAggregated) &&
    soldVehiclesInYears.length === 0
  ) {
    return null;
  }

  return (
    <AvoidBreakInside>
      <Card variant={isPrint ? 'inlineWhite' : undefined} data-testid={props['data-testid']}>
        <Box padding={2}>
          <VStack spacing={6}>
            <Align center>
              <Heading size={3} data-testid={suffixTestId('sectionHeading', props)}>
                {i18n.t('entity.overviewStatistics.labels.ageTitle')}
              </Heading>
            </Align>
            <Text
              align="center"
              size="small"
              color="secondary"
              data-testid={suffixTestId('ageDescription', props)}
            >
              <Trans
                i18nKey="entity.overviewStatistics.labels.ageDesciption"
                components={{bold: <Text key="bold" alternative inline size="small" />}}
              />
            </Text>
            <YearChart
              registrationYearChart={props.market?.registrationYearChart}
              vehicleYear={props.priceReport.vehicle.firstRegistrationOnYear}
              data-testid={suffixTestId('ageChart', props)}
            />
            <Hide when={soldVehiclesInYears.length === 0 || maxSoldVehiclesByYear === 0}>
              <Alert
                variant={isMyVehicleSuccesYear ? 'success' : 'warning'}
                icon={isMyVehicleSuccesYear ? 'action/trending_up' : 'action/trending_down'}
                data-testid={suffixTestId('ageAlert', props)}
              >
                <Trans
                  i18nKey={
                    isMyVehicleSuccesYear
                      ? 'entity.overviewStatistics.labels.ageAlertSuccess'
                      : 'entity.overviewStatistics.labels.ageAlertWarning'
                  }
                  values={{
                    mostSuccessYear: maxSoldVehiclesYears,
                    percentage: formatPercentage(
                      (soldVehiclesInMyYear ?? 0) / maxSoldVehiclesByYear,
                      'nearest'
                    ),
                  }}
                  components={{bold: <Text key="bold" alternative inline size="small" />}}
                />
              </Alert>
            </Hide>
          </VStack>
        </Box>
      </Card>
    </AvoidBreakInside>
  );
}
